import { Set } from '~commonInfra/utils/Set';
import { LinkedTasks } from '~src/linkedTasks/resources/LinkedTasks';
import { displayContentActionNotificationForLinkedTasks } from '~src/linkedTasks/utils/LinkedTaskHelpers';

import { Formatters, Messages } from '../../codegen/Localize';
import { simpleActionErrorMsg, simpleActionSuccessMsg } from '../../commonInfra/contentActions/ActionHelpers';
import { ContentActionNotification } from '../../commonInfra/contentActions/ContentActionNotification';
import { openReactConfirmationDialog } from '../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '../../commonInfra/L10n/L10n';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RunFlowTaskInfo } from '../models/RunFlowTaskInfo';
import { RunFlowTasks } from '../resources/RunFlowTasks';

export namespace RunFlowTasksAction {
  function runFlowOnOutputSteps(runFlowTaskInfos: Set<RunFlowTaskInfo>) {
    return RunFlowTasks.run(runFlowTaskInfos.keys());
  }
  function runLinkedTasks(linkedTaskInfos: Set<RunFlowTaskInfo>) {
    return LinkedTasks.runLinkedTasks(linkedTaskInfos.pluck('linkedTaskId'));
  }

  export function dialog(runFlowTaskInfos: Set<RunFlowTaskInfo>) {
    const taskCount = runFlowTaskInfos.size();
    const flowTasks = runFlowTaskInfos.filter(item => (item as RunFlowTaskInfo).taskType === 'FlowTask');
    const linkedTasks = runFlowTaskInfos.filter(item => (item as RunFlowTaskInfo).taskType === 'LinkedTask');
    let dialogBody;
    if (taskCount === 1) {
      const task = runFlowTaskInfos.first();
      if (linkedTasks.size() === 1) {
        dialogBody = Messages.linkedTaskRunPrompt();
      } else {
        dialogBody = Messages.runFlowTaskRunPrompt({
          FLOW_NAME: L10n.elideIfLong(task.flow.name)
        });
      }
    } else if (taskCount > 1) {
      if (linkedTasks.size() === 0) {
        dialogBody = Messages.runFlowTasksRunPrompt({
          ITEM_COUNT_LOC: Formatters.formatCount(taskCount)
        });
      } else if (flowTasks.size() === 0) {
        dialogBody = Messages.linkedTasksRunPrompt({
          ITEM_COUNT_LOC: Formatters.formatCount(taskCount)
        });
      } else {
        dialogBody = Messages.tasksRunPrompt({
          ITEM_COUNT_LOC: Formatters.formatCount(taskCount)
        });
      }
    }

    const runAction = () => {
      if (flowTasks.size() > 0 && linkedTasks.size() > 0) {
        return runFlowOnOutputSteps(flowTasks).then(() => {
          return runLinkedTasks(linkedTasks);
        });
      } else if (flowTasks.size() > 0) {
        return runFlowOnOutputSteps(flowTasks);
      } else if (linkedTasks.size() > 0) {
        return runLinkedTasks(linkedTasks);
      }
    };

    const dialogInstance = openReactConfirmationDialog<Set<RunFlowTaskInfo>, IResult>({
      title: Messages.runNow(),
      message: dialogBody,
      dialogWidth: TabDialogWidth.width375px,
      paramsForAction: runFlowTaskInfos,
      actionName: Messages.runNowConfirmButton(),
      action: runAction
    });

    dialogInstance.result.then((result: IResult) => {
      // Toasts for Flow Tasks
      if (flowTasks.size() > 0 && (linkedTasks.size() === 0 || result.errors.length === 0)) {
        ContentActionNotification.displaySingleToastForResult({
          items: flowTasks,
          errors: result.errors,
          successMsg: simpleActionSuccessMsg(Messages.runFlowTasksRunSuccess),
          errorMsg: simpleActionErrorMsg(Messages.runFlowTasksRunError),
        });
      }
      // Toasts for Linked Tasks
      if (linkedTasks.size() > 0 && (flowTasks.size() === 0 || result.errors.length === 0)) {
        ContentActionNotification.displaySingleToastForResult({
          items: linkedTasks,
          errors: result.errors,
          successMsg: simpleActionSuccessMsg(Messages.linkedTasksRunSuccess),
          errorMsg: simpleActionErrorMsg(Messages.linkedTasksRunError)
        });
      }
      // Toasts for Flow and Linked Tasks (with failures)
      if ((linkedTasks.size() > 0 && flowTasks.size() > 0) && result.errors.length > 0) {
        displayContentActionNotificationForLinkedTasks();
      }
    });

    return Promise.resolve(dialogInstance.result);
  }
}
